import {
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  ClockIcon,
  CopyIcon,
  HourglassIcon,
  Typography,
} from "@cpj-ai/panel-npm-package";
import { FlexCalendarCalendarEventStatusChoices, JobPositionInterviewType } from "@graphql/generated";
import { Button, Card, CardBody, CardHeader, Chip, Divider } from "@heroui/react";
import dayjs from "dayjs";
import React, { useState } from "react";
import toast from "react-hot-toast";
interface UpcomingInterviewCardProps {
  interviews?: JobPositionInterviewType[] | null;
}

const UpcomingInterviewCard: React.FC<UpcomingInterviewCardProps> = ({ interviews }) => {
  const [copied, setCopied] = useState(false);

  const activeInterview = interviews?.find(
    (interview) =>
      interview.event.status === FlexCalendarCalendarEventStatusChoices.SCHEDULED ||
      interview.event.status === FlexCalendarCalendarEventStatusChoices.STARTED,
  );

  if (!activeInterview) return null;

  const interviewStartTime = dayjs(activeInterview?.event.datetime[0]).format("HH:mm a");
  const interviewEndTime = dayjs(activeInterview?.event.datetime[1]).format("HH:mm a");
  const interviewDuration = dayjs(activeInterview?.event.datetime[1]).diff(
    dayjs(activeInterview?.event.datetime[0]),
    "minute",
  );

  const meetLink = activeInterview?.event.meetUrl;
  const formattedDate = dayjs(activeInterview?.event.datetime[0]).format("dddd, MMM D");
  const remainingDays = dayjs(activeInterview?.event.datetime[0]).diff(dayjs(), "day");

  const formattedTimeSlot = `${interviewStartTime} - ${interviewEndTime}`;

  const handleCopyLink = () => {
    if (!meetLink) return;

    navigator.clipboard.writeText(meetLink).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div className="bg-primary-400 rounded-xl pt-[3px]">
      <Card className="border border-zinc-200 overflow-visible">
        <CardHeader className="flex justify-between items-center gap-3 px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <CameraIcon className="w-5 h-6 stroke-primary-400 ml-1 mt-1" />
            </div>
            <div>
              <Typography className="text-sm text-default-500">Upcoming Interview</Typography>
            </div>
          </div>
          <Chip color={"danger"} variant="flat" size="sm">
            {remainingDays > 1 && `${remainingDays} days left`}
            {remainingDays === 1 && "Tomorrow"}
            {remainingDays === 0 && "Today"}
            {remainingDays < 0 && "Passed"}
          </Chip>
        </CardHeader>

        <Divider />

        <CardBody className="px-5 py-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="bg-cyan-700 bg-opacity-[0.08] rounded-full p-1.5">
                <CalendarIcon className="w-4 h-4 stroke-cyan-700" />
              </div>
              <div>
                <Typography className="text-xs text-default-500">Date</Typography>
                <Typography className="text-sm font-medium">{formattedDate}</Typography>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="bg-pink-600 bg-opacity-[0.08] rounded-full p-1.5">
                <ClockIcon className="w-4 h-4 fill-pink-600" />
              </div>
              <div>
                <Typography className="text-xs text-default-500">Time</Typography>
                <Typography className="text-sm font-medium">{formattedTimeSlot}</Typography>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="bg-green-700 bg-opacity-[0.08] rounded-full p-1.5">
                <CameraIcon className="w-4 h-4 stroke-green-700" />
              </div>
              <div>
                <Typography className="text-xs text-default-500">Type</Typography>
                <Typography className="text-sm font-medium">Online</Typography>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="bg-yellow-600 bg-opacity-[0.08] rounded-full p-1.5">
                <HourglassIcon className="w-4 h-4 stroke-yellow-600" />
              </div>
              <div>
                <Typography className="text-xs text-default-500">Duration</Typography>
                <Typography className="text-sm font-medium">{interviewDuration} min</Typography>
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 mb-1">
            <div className="flex items-center justify-between">
              <div className="flex-1 overflow-hidden">
                <Typography className="text-xs text-default-500 mb-1">Interview Link</Typography>
                <Typography className="text-sm font-mono truncate">{meetLink}</Typography>
              </div>
              <Button
                isIconOnly
                size="sm"
                radius="lg"
                variant="bordered"
                color={copied ? "success" : "primary"}
                className="ml-2 min-w-0"
                onPress={handleCopyLink}
                aria-label="Copy link"
              >
                {copied ? (
                  <CheckCircleIcon className="w-4 h-4 stroke-success-500" />
                ) : (
                  <CopyIcon className="w-4 h-4 fill-primary-500" />
                )}
              </Button>
            </div>
          </div>
          <Typography className="text-xs text-zinc-400">
            Link will be active 10 minutes before the interview. Please join the interview on time
          </Typography>
        </CardBody>
      </Card>
    </div>
  );
};

export default UpcomingInterviewCard;
